"use client";

import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { placeFloatingPanel, type SafeAreaInsets } from "../lib/floating-panel";

type Props = {
  id: string;
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
};

function readSafeArea(): SafeAreaInsets {
  const probe = document.createElement("div");
  probe.className = "safe-area-probe";
  document.body.append(probe);
  const style = getComputedStyle(probe);
  const insets = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

export function AnchoredPopover({ id, open, triggerRef, onClose, children, className = "", align = "end" }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState({ left: 8, top: 8, maxHeight: 320, visibility: "hidden" as "hidden" | "visible" });

  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;
    let frame = 0;
    const place = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const placement = placeFloatingPanel({
          trigger: trigger.getBoundingClientRect(),
          panel: panel.getBoundingClientRect(),
          viewport: { width: window.visualViewport?.width ?? window.innerWidth, height: window.visualViewport?.height ?? window.innerHeight },
          safeArea: readSafeArea(),
          align,
        });
        setStyle({ left: placement.left, top: placement.top, maxHeight: placement.maxHeight, visibility: "visible" });
        panel.dataset.side = placement.side;
      });
    };
    place();
    const resizeObserver = new ResizeObserver(place);
    resizeObserver.observe(panel);
    resizeObserver.observe(trigger);
    window.addEventListener("resize", place);
    window.addEventListener("orientationchange", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("orientationchange", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [align, open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, open, triggerRef]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div ref={panelRef} id={id} role="dialog" className={`anchored-popover ${className}`.trim()} style={style}>
      {children}
    </div>,
    document.body,
  );
}
